import {markRaw} from 'vue';
import {gmAPI, MapService} from '@/services/MapService';
import {addDomMutation} from '@/services/GraphicService/Graphic';


/** @type {number} Width of the border when highlighting */
const BORDER_WIDTH = 4;

/**
 * @typedef {object} OverlayViewGraphicData
 * @property {string} image A base64 representation of the graphic's image.
 * @property {google.maps.LatLngBoundsLiteral} bounds The geographic bounds of the image.
 * @property {string} id The id to be given to this graphic's DOM elements.
 * @property {number} [rotation] The degrees by which the image is rotated on the map.
 * @property {number} [zIndex] The z-index to be given to this graphic's DOM elements.
 * @property {string} [imagePath] A path to a file on Google Cloud Storage containing the image to display.
 */

class ImageOverlayView extends gmAPI.OverlayView {
  /**
   * Constructs a new ImageOverlayView.
   * @param {OverlayViewGraphicData} data
   */
  constructor({image, bounds, rotation, id, imagePath} = {}) {
    super();
    /** @type {number} */
    this.rotation = rotation || 0;
    /** @type {HTMLDivElement} */
    this.imgDiv = document.createElement('div');

    /** @type {HTMLDivElement} */
    this.clickDiv = document.createElement('div');
    this.clickDiv.id = id;
    const startIndex = imagePath?.indexOf('/') + 1;
    const endIndex = imagePath?.indexOf('-');
    this.clickDiv.setAttribute('data-testid', imagePath?.substring(startIndex || 0, endIndex || 0) ?? '');
    /** @type {HTMLImageElement} */
    this.image = document.createElement('img');
    this.imgDiv.appendChild(this.image);
    this.setImage(image, bounds);
    [this.imgDiv, this.clickDiv, this.image].forEach((element) => element.className = 'overlay');
    /** @type {boolean} indicates whether this image has been highlighted */
    this.highlighted = false;
    markRaw(this);
  }

  async onAdd() {
    // Add the DOM elements to the correct panes. This must happen in onAdd(), panes are not available until after
    // initialization.
    // The overlayMouseTarget pane is higher than the mapMarker pane. If a visible element is put into the
    // overlayMouseTarget pane, it will obscure the markers. So separate divs are needed for the overlay and click
    // target.
    const subPane = (await MapService.getSubPanes()).find((element) => element.id = 'overlay-graphics');
    if (subPane === undefined) {
      console.error(`ImageOverlayView: couldn't find the 'overlay-graphics' subpane to append SVG`);
      return;
    }
    const {overlayMouseTarget} = this.getPanes();
    addDomMutation(() => {
      overlayMouseTarget.appendChild(this.clickDiv);
      subPane.appendChild(this.imgDiv);
    });
  }

  // Called by Maps API when overlay is removed from the map.
  onRemove() {
    addDomMutation(() => {
      this.imgDiv.parentNode.removeChild(this.imgDiv);
      this.clickDiv.parentNode.removeChild(this.clickDiv);
    });
  }

  /** @param {boolean} enabled */
  setPointerEvents(enabled = true) {
    const pointerEvents = enabled ? 'auto' : 'none';
    addDomMutation(() => {
      this.imgDiv.style.pointerEvents = pointerEvents;
      this.clickDiv.style.pointerEvents = pointerEvents;
    });
  }

  // Called by Maps API when overlay is drawn to the map (includes redraws).
  draw() {
    const projection = this.getProjection();
    // The draw method is called when deleting a feature, so if no projection is available, return
    if (!projection) return;
    const sw = projection.fromLatLngToDivPixel(this.bounds.getSouthWest());
    const ne = projection.fromLatLngToDivPixel(this.bounds.getNorthEast());

    const width = ne.x - sw.x + (BORDER_WIDTH * 2);
    const height = sw.y - ne.y + (BORDER_WIDTH * 2);

    [this.imgDiv, this.clickDiv].forEach(({style}) => {
      // Execute the position mutations immediately to avoid flickering.
      style.left = `${sw.x - BORDER_WIDTH}px`;
      style.top = `${ne.y - BORDER_WIDTH}px`;
      style.width = `${width}px`;
      style.height = `${height}px`;
      // Batch the rest of the mutations for performance.
      addDomMutation(() => {
        // Display graphic only if width and height are at least 15px.
        style.display = (width >= 15 && height >= 15) ? 'block' : 'none';
        style.transform = `rotate(${-this.rotation}deg)`;
        style.borderWidth = `${BORDER_WIDTH}px`;
        style.borderStyle = 'solid';
        style.borderColor = (this.highlighted ? 'white' : 'transparent');
      });
    });
  }

  /**
   * Replaces the image and bounds of this graphic.
   * @param {string} image A base64 representation of the graphic's image.
   * @param {google.maps.LatLngBounds|google.maps.LatLngBoundsLiteral} bounds The geographic bounds of the image.
   */
  setImage(image, bounds) {
    /** @type {google.maps.LatLngBounds} */
    this.bounds = new gmAPI.LatLngBounds(
      new gmAPI.LatLng(bounds.south, bounds.west),
      new gmAPI.LatLng(bounds.north, bounds.east),
    );
    this.image.src = `data:image/png;base64,${image}`;
  }

  setHighlighted(highlighted) {
    this.highlighted = highlighted;
    [this.imgDiv, this.clickDiv].forEach(({style}) => {
      style.borderColor = (highlighted ? 'white' : 'transparent');
    });
  }

  /**
   * Sets the z-index of this graphic.
   * @param {number} zIndex
   */
  setZIndex(zIndex) {
    [this.imgDiv, this.clickDiv].forEach(({style}) => {
      style.zIndex = zIndex;
    });
  }

  setOptions(options) {
    if (options.image) {
      this.image = options.image;
    }
    if (options.bounds) {
      this.bounds = options.bounds;
    }
    if (options.rotation) {
      this.rotation = options.rotation;
    }
  }
}

export {ImageOverlayView};
